import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GPX_FEATURES } from '@rsos/gainsight';
import useHasLocationsToRender from '@rsos/hooks/useHasLocationsToRender';
import {
  useLazyGetConversationMetadataQuery,
  useLazyCreateConversationQuery,
  useCreateConversationOutboundSMSMutation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  useSelectedTextFrom911ConversationID,
  useSelectedTextFrom911Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { RootState } from '../../../../app';
import { trackStartTF911Response } from '../textFrom911Trackings';
import {
  startConversation,
  StartConversationResult,
} from './startConversation';

export interface UseTF911ConversationResult {
  /**
   * Single entry point for starting a TF911 conversation. If an active
   * (non-ended) conversation already exists, it is returned right away without
   * hitting any create endpoint. Otherwise a new conversation is started on the
   * appropriate channel (outbound SMS vs. location-based).
   */
  getOrStartConversation: (
    message?: string,
  ) => Promise<StartConversationResult>;
  conversationID: string | undefined;
  isConversationEnded: boolean;
  isOutboundSMSConversation: boolean | undefined;
  /** True once the initial conversation-metadata fetch has resolved. */
  isConversationMetadataReady: boolean;
}

/**
 * Owns all Text-From-911 conversation data plumbing for a selected caller: the
 * RTK triggers, their tracking side-effects, the initial metadata fetch, and
 * the single `getOrStartConversation` entry point shared by the start-chat
 * button, the preconfigured-message icons, and the live-video flows.
 */
export function useTF911Conversation(): UseTF911ConversationResult {
  const selectedCallerID = useSelector(
    (state: RootState) => state.emergencyCalls.selectedCallerID,
  );

  const hasLocationsToRender = useHasLocationsToRender();

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application ?? '',
      )?.name,
  );

  const conversationID = useSelectedTextFrom911ConversationID(
    selectedCallerID ?? '',
  );

  const conversation = useSelectedTextFrom911Conversation(
    selectedCallerID ?? '',
  );

  const isOutboundSMSConversation = useMemo(
    () => conversation?.data?.isOutboundSMS as boolean | undefined,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const [triggerGetConversationMetaData, getConversationMetaDataResult] =
    useLazyGetConversationMetadataQuery();

  const [triggerCreateConversation, createConversationResult] =
    useLazyCreateConversationQuery();

  const [
    triggerCreateOutboundSMSConversation,
    createOutboundSMSConversationResult,
  ] = useCreateConversationOutboundSMSMutation();

  useEffect(
    () => {
      // The conversation websocket (conversation.start/end) keeps the TF911
      // slice current, so when the conversation is already in the slice we can
      // trust it and skip the metadata GET. Only fetch when we don't have it
      // yet — a fresh load, a reload, or a conversation started elsewhere.
      if (selectedCallerID && !conversationID) {
        triggerGetConversationMetaData({
          callerID: selectedCallerID,
          orgName,
          custom: { isOutboundSMS: isOutboundSMSConversation },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCallerID],
  );

  useEffect(() => {
    if (['fulfilled', 'rejected'].includes(createConversationResult.status)) {
      trackStartTF911Response({
        feature: GPX_FEATURES.TF911,
        responseSuccess: createConversationResult.status === 'fulfilled',
        conversationId: createConversationResult?.data?.conversation_id ?? '',
      });
    }
  }, [createConversationResult]);

  useEffect(() => {
    if (
      ['fulfilled', 'rejected'].includes(
        createOutboundSMSConversationResult.status,
      )
    ) {
      trackStartTF911Response({
        feature: GPX_FEATURES.TF911_OUTBOUND,
        responseSuccess:
          createOutboundSMSConversationResult.status === 'fulfilled',
        conversationId:
          createOutboundSMSConversationResult?.data?.conversation_id ?? '',
      });
    }
  }, [createOutboundSMSConversationResult]);

  const getOrStartConversation = useCallback(
    async (message?: string): Promise<StartConversationResult> => {
      // An active (non-ended) conversation already exists, so reuse it instead
      // of re-hitting the create endpoint. This is the single source of truth
      // for that guard — live-video and start-chat callers no longer each
      // re-derive it.
      if (conversationID && !isConversationEnded) {
        return {
          isSuccess: true,
          data: { conversation_id: conversationID },
        };
      }

      return startConversation(message, {
        callerID: selectedCallerID,
        orgName,
        hasLocationsToRender,
        triggerCreateOutboundSMSConversation,
        triggerCreateConversation,
        triggerGetConversationMetaData,
      });
    },
    [
      conversationID,
      isConversationEnded,
      selectedCallerID,
      orgName,
      hasLocationsToRender,
      triggerCreateOutboundSMSConversation,
      triggerCreateConversation,
      triggerGetConversationMetaData,
    ],
  );

  return {
    getOrStartConversation,
    conversationID,
    isConversationEnded,
    isOutboundSMSConversation,
    // Ready when the metadata fetch has resolved, or when the websocket has
    // already put the conversation in the slice (so the fetch was skipped).
    isConversationMetadataReady:
      getConversationMetaDataResult.status === 'fulfilled' ||
      Boolean(conversationID),
  };
}
